import { useState, useCallback } from 'react';
import { saveSession, getSession, deleteSession } from '../lib/storage';

export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  const checkSession = useCallback(async () => {
    const session = await getSession();
    if (session) {
      setUserId(session.userId);
      setIsAuthenticated(true);
    } else {
      // Also check localStorage for userId
      const storedUserId = localStorage.getItem('userId');
      const accessToken = localStorage.getItem('accessToken');
      if (storedUserId && accessToken) {
        setUserId(storedUserId);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    }
    return session;
  }, []);

  const setAccessToken = useCallback((token: string) => {
    localStorage.setItem('accessToken', token);
  }, []);

  const setRefreshToken = useCallback((token: string) => {
    localStorage.setItem('refreshToken', token);
  }, []);

  const getRefreshToken = useCallback(() => {
    return localStorage.getItem('refreshToken');
  }, []);

  const login = useCallback(async (
    userId: string,
    scratchUsername: string,
    accessToken: string,
    refreshToken: string,
    encryptedSk: string,
    salt: string
  ) => {
    await saveSession({
      id: 'current',
      userId,
      scratchUsername,
      encryptedSk,
      salt,
      sessionKey: '',
      createdAt: new Date().toISOString()
    });
    setAccessToken(accessToken);
    setRefreshToken(refreshToken);
    setUserId(userId);
    setIsAuthenticated(true);
  }, [setAccessToken, setRefreshToken]);

  const logout = useCallback(async () => {
    await deleteSession();
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUserId(null);
    setIsAuthenticated(false);
  }, []);

  return {
    isAuthenticated,
    userId,
    checkSession,
    login,
    logout,
    setAccessToken,
    setRefreshToken,
    getRefreshToken
  };
}